/**
 * Constructor browse facets — same CSS surface as Shopify native (component-facets.css).
 */
class ConstructorFacets extends HTMLElement {
  static #TRUTHY = new Set(['true', '1', 'yes']);
  static #FALSY = new Set(['false', '0', 'no']);

  // Apparel letter sizes — keeps Constructor Size options in a natural chart order
  // instead of Constructor's default popularity / match-count order.
  static #LETTER_SIZE_RANK = {
    xxxxs: 1,
    xxxs: 2,
    xxs: 3,
    xs: 4,
    s: 10,
    's/m': 15,
    sm: 15,
    m: 20,
    'm/l': 25,
    ml: 25,
    l: 30,
    'l/xl': 35,
    lxl: 35,
    xl: 40,
    xxl: 50,
    '2xl': 50,
    xxxl: 60,
    '3xl': 60,
    '4xl': 70,
    '5xl': 80,
    '6xl': 90,
  };

  constructor() {
    super();
    this._selected = Object.create(null);
    this._lastFacets = [];
    this._mappingReady = false;
    this._onBrowseData = this._onBrowseData.bind(this);
    this._onDelegClick = this._onDelegClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onClearAll = this._onClearAll.bind(this);
    this._onBarChange = this._onBarChange.bind(this);
    this._onBarClick = this._onBarClick.bind(this);
    this._onDesktopBarClick = this._onDesktopBarClick.bind(this);
    this._onAccordionClick = this._onAccordionClick.bind(this);
    this._refreshFadeouts = this._refreshFadeouts.bind(this);
    this._onDrawerClick = this._onDrawerClick.bind(this);
    this._onApplyDrawer = this._onApplyDrawer.bind(this);
  }

  connectedCallback() {
    this._browse = this.closest('constructor-browse') || this.closest('constructor-search');
    if (!this._browse) return;

    this._constructorDataEvent =
      this._browse.tagName === 'CONSTRUCTOR-SEARCH' ? 'constructor-search:data' : 'constructor-browse:data';
    this._constructorApplyEvent =
      this._browse.tagName === 'CONSTRUCTOR-SEARCH'
        ? 'constructor-search:apply-filters'
        : 'constructor-browse:apply-filters';

    const tplRoot = this.querySelector('.constructor-facets-templates');
    this._checkIconHtml = tplRoot?.querySelector('.js-ref-check-icon')?.innerHTML ?? '';
    this._caretHtml = tplRoot?.querySelector('.js-ref-caret-icon')?.innerHTML ?? '';
    this._arrowHtml = tplRoot?.querySelector('.js-ref-arrow-icon')?.innerHTML ?? '';
    this._chipRemoveHtml = tplRoot?.querySelector('.js-ref-chip-remove')?.innerHTML ?? '';

    this.removeAttribute('hidden');
    this._browse.addEventListener(this._constructorDataEvent, this._onBrowseData);
    this.addEventListener('change', this._onChange);
    this._browse.addEventListener('click', this._onDelegClick);
    this.addEventListener('click', this._onAccordionClick);
    window.addEventListener('resize', this._refreshFadeouts);
    this._clearTriggers = [
      ...this.querySelectorAll('[data-action="constructor-facets-clear"]'),
      ...(this._browse?.querySelectorAll('[data-action="constructor-facets-clear"]') || []),
    ];
    this._clearTriggers.forEach((el) => el.addEventListener('click', this._onClearAll));

    this.innerContainer = this.querySelector('[data-constructor-facet-groups]');
    this._countLines = [...this.querySelectorAll('.js-constructor-product-count-line')];
    this._footerCountLines = [...(this._browse?.querySelectorAll('.js-constructor-footer-product-count') || [])];
    this.chipsRoot = this._browse?.querySelector('[data-constructor-chips]');
    this.activeWrap = this._browse?.querySelector('[data-constructor-active-wrap]');
    this.activeHeading = this._browse?.querySelector('[data-constructor-active-heading]');
    this._chipsClear = this._browse?.querySelector('[data-constructor-chips-clear]');
    this._productCountEl = this.querySelector('[data-constructor-product-count]');

    this.barRoot = this._browse.querySelector('[data-constructor-bar]');
    this.barMobileRoot = this._browse.querySelector('[data-constructor-bar-mobile]');
    this.barRoot?.addEventListener('change', this._onBarChange);
    this.barRoot?.addEventListener('click', this._onDesktopBarClick);
    this.barMobileRoot?.addEventListener('change', this._onBarChange);
    this.barMobileRoot?.addEventListener('click', this._onBarClick);

    this._drawerRoot = this._browse.querySelector('.mobile-facets__disclosure');
    this._drawerRoot?.addEventListener('click', this._onDrawerClick);

    this._applyBtn = this._browse.querySelector('.js-constructor-apply-drawer');
    this._applyBtn?.addEventListener('click', this._onApplyDrawer);

    this._hydrateFromUrl();
  }

  disconnectedCallback() {
    if (this._constructorDataEvent) {
      this._browse?.removeEventListener(this._constructorDataEvent, this._onBrowseData);
    }
    this._browse?.removeEventListener('click', this._onDelegClick);
    this.removeEventListener('click', this._onAccordionClick);
    this.removeEventListener('change', this._onChange);
    window.removeEventListener('resize', this._refreshFadeouts);
    this._clearTriggers?.forEach((el) => el.removeEventListener('click', this._onClearAll));
    this.barRoot?.removeEventListener('change', this._onBarChange);
    this.barRoot?.removeEventListener('click', this._onDesktopBarClick);
    this.barMobileRoot?.removeEventListener('change', this._onBarChange);
    this.barMobileRoot?.removeEventListener('click', this._onBarClick);
    this._drawerRoot?.removeEventListener('click', this._onDrawerClick);
    this._applyBtn?.removeEventListener('click', this._onApplyDrawer);
  }

  _isBooleanFacet(facet) {
    const opts = facet?.options || [];
    if (opts.length === 0 || opts.length > 2) return false;
    return opts.every((o) => {
      const v = String(o.value).toLowerCase();
      return ConstructorFacets.#TRUTHY.has(v) || ConstructorFacets.#FALSY.has(v);
    });
  }

  _booleanTruthOption(facet) {
    return (facet?.options || []).find((o) => ConstructorFacets.#TRUTHY.has(String(o.value).toLowerCase()));
  }

  // After data arrives, replace any URL-hydrated truthy variants ('true', 'True', '1', ...)
  // with Constructor's actual truthy option value (case-preserving). Keeps _selected canonical.
  _normalizeBooleanSelections(facets) {
    (facets || []).forEach((facet) => {
      if (!facet?.name || !this._isBooleanFacet(facet)) return;
      const set = this._selected[facet.name];
      if (!set || set.size === 0) return;
      const actual = String(this._booleanTruthOption(facet)?.value ?? '');
      if (!actual) return;
      const next = new Set();
      set.forEach((v) => {
        next.add(ConstructorFacets.#TRUTHY.has(String(v).toLowerCase()) ? actual : v);
      });
      this._selected[facet.name] = next;
    });
  }

  // Memoised once per component instance; assumes window.constructorFacetsConfig is page-load constant.
  _facetConfig(name) {
    if (!this._facetCfgMap) {
      this._facetCfgMap = Object.create(null);
      (window.constructorFacetsConfig || []).forEach((c) => {
        if (c?.name) this._facetCfgMap[c.name] = c;
      });
    }
    return this._facetCfgMap[name] || null;
  }

  // Set of every shopify_filter_param declared in constructorFacetsConfig.
  // Used by _pushUrlState to scope URL cleanup to params Constructor actually manages,
  // so non-Constructor filter.* params (other apps, campaigns, legacy URLs) are preserved.
  _managedFilterParams() {
    if (!this._managedFilterParamsSet) {
      this._managedFilterParamsSet = new Set(
        (window.constructorFacetsConfig || []).map((c) => String(c?.shopifyFilterParam || '').trim()).filter(Boolean),
      );
    }
    return this._managedFilterParamsSet;
  }

  _facetDisplayName(name) {
    return this._facetConfig(name)?.displayName || this._facetDisplayNames?.[name] || name;
  }

  _isSizeFacet(name) {
    if (/size/i.test(String(name || ''))) return true;
    const cfg = this._facetConfig(name);
    if (cfg?.shopifyFilterParam && /size/i.test(String(cfg.shopifyFilterParam))) return true;
    if (cfg?.displayName && /size/i.test(String(cfg.displayName))) return true;
    return false;
  }

  /**
   * Sort key: [group, primary, secondary]
   * 0 = letter sizes (XS…2XL), 1 = age/kids (3-4 y), 2 = numeric/ranges, 3 = universal/other.
   */
  _sizeSortKey(raw) {
    const label = String(raw ?? '').trim();
    const lc = label.toLowerCase().replace(/\s+/g, ' ').trim();
    const compact = lc.replace(/\s+/g, '');

    if (/universal|one\s*size|onesize|^os$|^uni$/.test(lc)) {
      return [3, 9999, lc];
    }

    const letterRank = ConstructorFacets.#LETTER_SIZE_RANK[compact];
    if (letterRank != null) return [0, letterRank, lc];

    // Kids / age: "3-4 y", "2-4 r", "6-8r"
    const age = compact.match(/^(\d+)[-–](\d+)([a-z])$/i);
    if (age && /[yr]/i.test(age[3])) {
      return [1, parseInt(age[1], 10), parseInt(age[2], 10)];
    }

    // Numbers and ranges: "36", "36-37", "35-38"
    const num = compact.match(/^(\d+(?:[.,]\d+)?)(?:[-–](\d+(?:[.,]\d+)?))?$/);
    if (num) {
      const start = parseFloat(num[1].replace(',', '.'));
      const end = num[2] ? parseFloat(num[2].replace(',', '.')) : start;
      return [2, start, end];
    }

    return [3, 0, lc];
  }

  _sortedFacetOptions(facet) {
    const opts = [...(facet?.options || [])];
    if (!this._isSizeFacet(facet?.name)) return opts;
    return opts.sort((a, b) => {
      const ka = this._sizeSortKey(a?.display_name || a?.value);
      const kb = this._sizeSortKey(b?.display_name || b?.value);
      for (let i = 0; i < 3; i++) {
        if (ka[i] < kb[i]) return -1;
        if (ka[i] > kb[i]) return 1;
      }
      return 0;
    });
  }

  _findIconEntry(rawValue) {
    const map = window.constructorFiltersMapping;
    if (!map) return null;
    const key = String(rawValue);
    if (map[key]) return map[key];

    // Constructor often returns metaobject refs as `gid://shopify/Metaobject/40196`
    // or the bare numeric id; try the trailing digits.
    const trailing = key.match(/(\d+)\s*$/);
    if (trailing && map[trailing[1]]) return map[trailing[1]];

    // Last resort: case-insensitive label match.
    const lc = key.toLowerCase();
    for (const code in map) {
      if (map[code]?.label && String(map[code].label).toLowerCase() === lc) return map[code];
    }
    return null;
  }

  _resolveOptionLabel(rawValue, displayFromApi) {
    const entry = this._findIconEntry(rawValue);
    if (entry?.label) return String(entry.label);
    if (displayFromApi != null && String(displayFromApi).trim() !== '') {
      return String(displayFromApi);
    }
    return String(rawValue);
  }

  _optionLabel(name, rawValue, displayFromApi) {
    const override = this._facetConfig(name)?.optionLabels?.[rawValue];
    if (override) return override;
    return this._resolveOptionLabel(rawValue, displayFromApi);
  }

  _optionIconHtml(rawValue, { overlayCheck = false } = {}) {
    const entry = this._findIconEntry(rawValue);
    if (!entry) return '';
    const image = entry.image ? String(entry.image).replace(/'/g, "\\'") : '';
    const color = entry.color ? String(entry.color).replace(/"/g, '&quot;') : '';
    const bg = image ? `url('${image}')` : color;
    if (!bg) return '';
    const lightClass =
      !entry.image && ConstructorFacets.isLightCssColor(entry.color) ? ' facets__item_label_icon--light' : '';
    const checkInner = overlayCheck
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" fill="currentColor" focusable="false" aria-hidden="true">
          <path d="M11.6 3.4 5.5 9.5 2.4 6.4 1 7.8l4.5 4.5 7.5-7.5z"/>
        </svg>`
      : '';
    return `<div class="facets__item_label_icon${lightClass}" style="background:${bg};background-size:contain;">
      <span class="icon-checkmark-metafield" aria-hidden="true">${checkInner}</span>
    </div>`;
  }

  /** WCAG relative luminance — light swatches get a purple check, dark ones stay white. */
  static isLightCssColor(input, threshold = 0.55) {
    const rgb = ConstructorFacets.parseCssColorToRgb(input);
    if (!rgb) return false;
    const [r, g, b] = rgb.map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > threshold;
  }

  static parseCssColorToRgb(input) {
    if (input == null) return null;
    let s = String(input).trim();
    if (!s || /^url\(/i.test(s)) return null;
    // Inline styles may be "background: #fc0; background-size: contain" — take first color-ish token
    s = s
      .split(';')[0]
      .replace(/^background(-color)?\s*:\s*/i, '')
      .trim();
    if (/^url\(/i.test(s)) return null;

    let m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (m) {
      let h = m[1];
      if (h.length === 3) h = [...h].map((c) => c + c).join('');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }

    m = s.match(/^rgba?\(\s*([\d.]+)\s*[,/ ]\s*([\d.]+)\s*[,/ ]\s*([\d.]+)/i);
    if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
    return null;
  }

  _isTwoColumnDesktopFacet(facetName) {
    return facetName === 'color' || facetName === 'motive_id';
  }

  _facetKind(facet) {
    const type = this._facetConfig(facet?.name)?.displayType;
    if (type === 'toggle' || this._isBooleanFacet(facet)) return 'boolean';
    const opts = facet?.options || [];
    const hasIcon = opts.some((o) => {
      const e = this._findIconEntry(String(o.value));
      return e && (e.image || e.color);
    });
    return hasIcon ? 'metafield' : 'list';
  }

  _onDrawerClick(event) {
    const target = event.target;
    if (!target) return;

    const submenuTrigger = target.closest?.('.mobile-facets__close-button, .mobile-facets__back-button');
    if (submenuTrigger) {
      const details = submenuTrigger.closest('details.mobile-facets__details');
      if (details) {
        event.preventDefault();
        this._closeSubmenu(details);
        return;
      }
    }

    const isCloseTrigger =
      target.closest?.('.js-details-close') ||
      target.classList?.contains('mobile-facets') ||
      target.classList?.contains('mobile_sort_container');
    if (!isCloseTrigger || target.closest?.('.mobile-facets__show-more')) return;
    this._closeDrawer();
  }

  _closeSubmenu(details) {
    details.classList.remove('menu-opening');
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    setTimeout(() => details.removeAttribute('open'), ConstructorBase.DRAWER_CLOSE_MS);
  }

  _onApplyDrawer(event) {
    event.preventDefault();
    this._broadcastFilters();
    this._closeDrawer();
  }

  _closeDrawer() {
    if (this._drawerRoot) FocusLayer.pop(this._drawerRoot);
    this._drawerRoot?.removeAttribute('open');
    this._drawerRoot?.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
  }

  async _onBrowseData(event) {
    const response = event.detail?.response;
    const facets = Array.isArray(response?.facets) ? response.facets : [];
    this._lastFacets = facets;
    this._normalizeBooleanSelections(facets);
    this._facetDisplayNames = facets.reduce((acc, f) => {
      if (f?.name) acc[f.name] = f.display_name || f.name;
      return acc;
    }, {});
    const total = typeof response?.total_num_results === 'number' ? response.total_num_results : null;
    if (total !== null) this._syncProductCounts(total);

    if (window.constructorFiltersMappingReady) {
      try {
        await window.constructorFiltersMappingReady;
      } catch (_) {
        // proceed with whatever we have
      }
    }
    // Gate first chip render until the metaobject mapping is resolved, otherwise
    // _syncActiveChipsUi (called from _syncProductCounts above) flashes raw IDs on
    // cold-cache loads when Constructor returns no options for active filters.
    this._mappingReady = true;

    this._optionDisplayNames = facets.reduce((acc, f) => {
      if (!f?.name) return acc;
      const map = (acc[f.name] = {});
      (f.options || []).forEach((o) => {
        if (o?.value != null) {
          map[String(o.value)] = this._resolveOptionLabel(String(o.value), o.display_name);
        }
      });
      return acc;
    }, {});

    const ordered = this._orderedFacets(facets);
    this._renderBar(ordered);

    const container = this.innerContainer;
    if (!container) return;

    // A group the customer expanded with "Show all" stays expanded across the re-render.
    const openFacets = new Set(
      [...container.querySelectorAll('[data-facet-name] .mobile-facets__metafields_preview.open')].map(
        (preview) => preview.closest('[data-facet-name]').dataset.facetName,
      ),
    );

    container.innerHTML = ordered.map((facet, idx) => this._facetBlockHtml(facet, idx)).join('');

    container.querySelectorAll('[data-facet-name]').forEach((block) => {
      if (openFacets.has(block.dataset.facetName)) {
        block.querySelector('.mobile-facets__metafields_preview')?.classList.add('open');
      }
    });

    if (container.childElementCount) {
      this.classList.remove('constructor-facets--empty');
    } else {
      this.classList.add('constructor-facets--empty');
    }

    this._allFacetInputs().forEach((cb) => {
      const name = cb.dataset.facetName;
      const val = cb.dataset.value;
      const set = this._selected[name];
      cb.checked = Boolean(set && set.has(val));
    });

    this._observeFadeouts();
    this._initCollapsedLists();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _onAccordionClick(event) {
    const showMore = event.target.closest('.mobile-facets__show-more');
    if (!showMore || !this.contains(showMore)) return;
    event.preventDefault();
    event.stopPropagation();
    const preview = showMore.closest('.mobile-facets__metafields_preview');
    preview?.classList.toggle('open');
    this._initCollapsedLists(preview);
  }

  _initCollapsedLists(root) {
    const scope = root || this.innerContainer;
    if (!scope) return;
    const maxItems = 5;
    const previews = scope.matches?.('.mobile-facets__metafields_preview')
      ? [scope]
      : [...scope.querySelectorAll('.mobile-facets__metafields_preview')];

    previews.forEach((preview) => {
      const list = preview.querySelector('.mobile-facets__list');
      const btn = preview.querySelector('.mobile-facets__show-more');
      if (!list) return;

      const items = [...list.querySelectorAll('.mobile-facets__item')].filter(
        (item) => !item.classList.contains('hidden'),
      );
      const isOpen = preview.classList.contains('open');
      items.forEach((item, index) => {
        item.classList.toggle('mobile-facets__item--collapsed', !isOpen && index >= maxItems);
      });
      if (btn) {
        btn.classList.toggle('hidden', items.length <= maxItems);
        const labelEl = btn.querySelector('.mobile-facets__show-more-label');
        const more = btn.dataset.labelMore;
        const less = btn.dataset.labelLess;
        if (labelEl && more && less) labelEl.textContent = isOpen ? less : more;
      }
    });
  }

  _observeFadeouts() {
    if (!this.innerContainer) return;
    this._fadeoutHandlers = this._fadeoutHandlers || new WeakMap();

    this.innerContainer.querySelectorAll('.mobile-facets__list').forEach((list) => {
      if (this._fadeoutHandlers.has(list)) return;
      const container = list.parentElement;
      const update = () => {
        if (!container) return;
        const overflow = list.scrollWidth > list.offsetWidth;
        if (!overflow) {
          container.classList.remove('fadeout_left', 'fadeout_right');
          return;
        }
        container.classList.toggle('fadeout_left', list.scrollLeft > 50);
        container.classList.toggle('fadeout_right', list.scrollLeft + list.offsetWidth < list.scrollWidth - 50);
      };
      update();
      list.addEventListener('scroll', update);
      this._fadeoutHandlers.set(list, update);
    });
  }

  _refreshFadeouts() {
    if (!this.innerContainer || !this._fadeoutHandlers) return;
    this.innerContainer.querySelectorAll('.mobile-facets__list').forEach((list) => {
      const handler = this._fadeoutHandlers.get(list);
      if (handler) handler();
    });
  }

  _syncProductCounts(total) {
    const strings = window.constructorStrings || {};
    const simpleTemplate =
      total === 1
        ? strings.facetProductCountTplOne || strings.facetProductCountTpl
        : strings.facetProductCountTplOther || strings.facetProductCountTpl;
    let simpleText = '';
    if (simpleTemplate && simpleTemplate.includes('__count__')) {
      simpleText = simpleTemplate.replace(/\b__count__\b/g, String(total));
    } else if (simpleTemplate) {
      simpleText = simpleTemplate;
    } else {
      simpleText = String(total);
    }
    this._countLines.forEach((el) => {
      if (!el) return;
      el.textContent = simpleText;
      el.hidden = false;
    });
    this._footerCountLines.forEach((el) => {
      if (!el) return;
      el.textContent = `(${total})`;
      el.classList.remove('visually-hidden');
    });
    this._productCountEl?.classList?.remove('hidden');
    this._syncActiveChipsUi();
  }

  _facetBlockHtml(facet, idx) {
    switch (this._facetKind(facet)) {
      case 'boolean':
        return this._renderToggleBlock(facet, idx);
      case 'metafield':
        return this._renderMetafieldBlock(facet, idx);
      default:
        return this._renderListBlock(facet, idx);
    }
  }

  _renderToggleBlock(facet, idx) {
    const tpl = document.getElementById('constructor-facets-toggle-block');
    if (!tpl) return '';
    const truthOpt = this._booleanTruthOption(facet);
    if (!truthOpt) return '';

    const facetName = facet.name;
    const sid = this.dataset.sectionId || '';
    const safeId = `Filter-constructor-drawer-${facetName}-${sid}-${idx}`.replace(/[^a-zA-Z0-9_-]/g, '');
    const truthVal = String(truthOpt.value);
    const checked = this._selected[facetName]?.has(truthVal);

    const clone = tpl.content.firstElementChild.cloneNode(true);
    clone.id = `Details-Constructor-${idx}-${sid}`;
    clone.dataset.index = `constructor-${idx}`;
    clone.dataset.facetName = facetName;

    const titleEl = clone.querySelector('[data-constructor-toggle-title]');
    if (titleEl) titleEl.textContent = this._facetDisplayName(facetName);

    const labelEl = clone.querySelector('[data-constructor-toggle-label]');
    if (labelEl) labelEl.setAttribute('for', safeId);

    const input = clone.querySelector('[data-constructor-toggle-input]');
    if (input) {
      input.id = safeId;
      input.dataset.facetName = facetName;
      input.dataset.value = truthVal;
      // outerHTML reflects attributes, not the `checked` IDL property — use the attribute.
      if (checked) input.setAttribute('checked', '');
    }

    // _facetBlockHtml joins string results, so serialize back. Browser handles escaping.
    return clone.outerHTML;
  }

  _showMoreButtonHtml() {
    const showAll = String(
      window.constructorStrings?.facetShowAll || window.constructorStrings?.facetShowMore || 'Show all',
    ).replace(/</g, '&lt;');
    const showLess = String(window.constructorStrings?.facetShowLess || 'Show less').replace(/</g, '&lt;');
    return `<button type="button" class="mobile-facets__show-more hidden" data-label-more="${showAll}" data-label-less="${showLess}"><span class="mobile-facets__show-more-label">${showAll}</span>${this._caretHtml}</button>`;
  }

  _optionItemsHtml(facet, idx, idPrefix, { skipHidden = false } = {}) {
    const facetName = facet.name;
    const sid = this.dataset.sectionId || '';
    const kind = this._facetKind(facet);

    return this._sortedFacetOptions(facet)
      .map((opt, optIdx) => {
        const value = String(opt.value);
        const entry = this._findIconEntry(value);
        const hasIcon = !!(entry && (entry.image || entry.color));
        const label = String(this._optionLabel(facetName, value, opt.display_name)).replace(/</g, '&lt;');
        const count = typeof opt.count === 'number' ? opt.count : '';
        const safeId = `Filter-constructor-${idPrefix}-${facetName}-${sid}-${idx}-${optIdx}`.replace(
          /[^a-zA-Z0-9_-]/g,
          '',
        );
        const disabled = typeof opt.count === 'number' && opt.count <= 0;
        const hasSel = this._selected[facetName]?.has(value);
        const dimmed = disabled && !hasSel;
        // Color facet options may use a solid hex or an image swatch (e.g. Multicolor).
        // Motive/other icon facets keep image rows without `facets__color`.
        const isColorFacet = /color/i.test(String(facetName));
        const isColor = isColorFacet
          ? !!(entry && (entry.color || entry.image))
          : !!(entry && entry.color && !entry.image);
        const iconHtml = this._optionIconHtml(value, { overlayCheck: isColor });
        const checkedAttr = hasSel ? 'checked' : '';
        const disabledAttr = dimmed ? 'disabled aria-disabled="true"' : '';
        const valueAttr = value.replace(/"/g, '&quot;');
        const showRowCheck = !isColor;
        const checkHtml = showRowCheck
          ? this._checkIconHtml || '<span class="checkbox__box" aria-hidden="true"></span>'
          : '';
        const colorClass = isColor ? ' facets__color' : '';
        const checkboxClass = showRowCheck ? ' checkbox' : '';
        const disabledClass = dimmed
          ? ` mobile-facets__label--disabled${showRowCheck ? ' checkbox--disabled' : ''}`
          : '';
        const hidden = !hasIcon || dimmed;

        if (skipHidden && hidden) return '';

        if (kind === 'metafield') {
          return `
            <li class="mobile-facets__item list-menu__item${hidden ? ' hidden' : ''}">
              <label
                for="${safeId}"
                class="mobile-facets__label${checkboxClass}${disabledClass}${colorClass}"
              >
                <input
                  type="checkbox"
                  id="${safeId}"
                  class="checkbox__input mobile-facets__checkbox"
                  data-facet-name="${facetName}"
                  data-value="${valueAttr}"
                  ${disabledAttr}
                  ${checkedAttr}
                >
                ${checkHtml}
                ${iconHtml}
                <span aria-hidden="true">${label}<span class="mobile-facets__item_count"> (${count})</span></span>
              </label>
            </li>
          `;
        }

        if (skipHidden && dimmed) return '';

        return `
          <li class="mobile-facets__item list-menu__item${dimmed ? ' hidden' : ''}">
            <label
              for="${safeId}"
              class="checkbox mobile-facets__label${dimmed ? ' checkbox--disabled mobile-facets__label--disabled' : ''}"
            >
              <input
                type="checkbox"
                id="${safeId}"
                class="checkbox__input mobile-facets__checkbox"
                data-facet-name="${facetName}"
                data-value="${valueAttr}"
                ${disabledAttr}
                ${checkedAttr}
              >
              ${checkHtml}
              <span aria-hidden="true">${label}<span class="mobile-facets__item_count"> (${count})</span></span>
            </label>
          </li>
        `;
      })
      .join('');
  }

  _optionsListHtml(facet, idx, idPrefix) {
    const skipHidden = idPrefix === 'desktop' && this._isTwoColumnDesktopFacet(facet.name);
    const itemsHtml =
      this._optionItemsHtml(facet, idx, idPrefix, { skipHidden }) ||
      `<li><p class="caption">${window.constructorStrings?.facetNoValues || ''}</p></li>`;
    return `<ul class="mobile-facets__list list-unstyled" role="list">${itemsHtml}</ul>`;
  }

  _renderAccordionBlock(facet, idx) {
    const facetName = facet.name;
    const title = String(this._facetDisplayName(facetName)).replace(/</g, '&lt;');
    const sid = this.dataset.sectionId || '';
    const selectedN = this._selected[facetName]?.size ?? 0;
    const selectedClass = selectedN ? '' : ' hidden';
    const selectedText = selectedN ? String(selectedN) : '';
    const kind = this._facetKind(facet);
    const extraClass = kind === 'metafield' ? '' : ' constructor-facet-list';

    return `
      <div
        id="Details-Constructor-${idx}-${sid}"
        class="mobile_accordion mobile-facets__details_metafields js-filter constructor-facet-details${extraClass}"
        data-index="constructor-${idx}"
        data-facet-name="${facetName}"
      >
        <div class="mobile_accordion_header">
          <span>${title}</span>
          <span class="facets__selected${selectedClass}">${selectedText}</span>
        </div>
        <div class="mobile-facets__metafields_preview">
          ${this._optionsListHtml(facet, idx, 'drawer')}
          ${this._showMoreButtonHtml()}
        </div>
      </div>
    `;
  }

  _renderMetafieldBlock(facet, idx) {
    return this._renderAccordionBlock(facet, idx);
  }

  _renderListBlock(facet, idx) {
    return this._renderAccordionBlock(facet, idx);
  }

  _orderedFacets(facets) {
    const legacyOrder = (window.constructorFacetOrder || []).map((s) => String(s).trim()).filter(Boolean);
    const rank = (name) => {
      const cfg = this._facetConfig(name);
      if (cfg && Number.isFinite(cfg.position)) return cfg.position;
      const i = legacyOrder.indexOf(name);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    const visible = (facets || []).filter((facet) => facet && !facet.hidden && facet.name);
    visible.sort((a, b) => rank(a.name) - rank(b.name));
    return visible;
  }

  _appendCaret(host) {
    if (!host || !this._caretHtml) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = this._caretHtml;
    while (tmp.firstChild) host.appendChild(tmp.firstChild);
  }

  _cloneBarToggle(facet, idSuffix) {
    const toggleTpl = document.getElementById('constructor-facets-bar-toggle');
    if (!toggleTpl) return null;
    const truthOpt = this._booleanTruthOption(facet);
    if (!truthOpt) return null;

    const facetName = facet.name;
    const sid = this.dataset.sectionId || '';
    const safeId = `Filter-constructor-bar-${idSuffix}-${facetName}-${sid}`.replace(/[^a-zA-Z0-9_-]/g, '');
    const truthVal = String(truthOpt.value);
    const checked = this._selected[facetName]?.has(truthVal);
    const clone = toggleTpl.content.firstElementChild.cloneNode(true);
    clone.dataset.facetName = facetName;

    const lbl = clone.querySelector('[data-constructor-bar-toggle-label]');
    if (lbl) {
      lbl.setAttribute('for', safeId);
      lbl.textContent = this._facetDisplayName(facetName);
    }

    const inputLbl = clone.querySelector('[data-constructor-bar-toggle-input-label]');
    if (inputLbl) inputLbl.setAttribute('for', safeId);

    const input = clone.querySelector('[data-constructor-bar-toggle-input]');
    if (input) {
      input.id = safeId;
      input.dataset.facetName = facetName;
      input.dataset.value = truthVal;
      if (checked) input.checked = true;
    }

    return clone;
  }

  _cloneDesktopDropdown(facet, idx) {
    const dropdownTpl = document.getElementById('constructor-facets-dropdown');
    if (!dropdownTpl) return null;

    const facetName = facet.name;
    const sid = this.dataset.sectionId || '';
    const selectedN = this._selected[facetName]?.size ?? 0;
    const clone = dropdownTpl.content.firstElementChild.cloneNode(true);
    clone.id = `Details-Constructor-Desktop-${idx}-${sid}`;
    clone.dataset.index = `constructor-desktop-${idx}`;
    clone.dataset.facetName = facetName;

    const titleEl = clone.querySelector('[data-constructor-bar-title]');
    if (titleEl) titleEl.textContent = this._facetDisplayName(facetName);

    const selectedEl = clone.querySelector('[data-constructor-bar-selected]');
    if (selectedEl && selectedN > 0) {
      selectedEl.textContent = String(selectedN);
      selectedEl.classList.remove('hidden');
    }

    const applyCount = clone.querySelector('[data-constructor-dropdown-apply-count]');
    if (applyCount && selectedN > 0) {
      applyCount.textContent = `(${selectedN})`;
      applyCount.classList.remove('hidden');
    }

    const optionsHost = clone.querySelector('[data-constructor-dropdown-options]');
    if (optionsHost) optionsHost.innerHTML = this._optionsListHtml(facet, idx, 'desktop');

    const panel = clone.querySelector('[data-constructor-dropdown-panel]');
    if (panel && this._isTwoColumnDesktopFacet(facetName)) {
      panel.classList.add('facets__filter-values--columns-2');
    }

    this._appendCaret(clone.querySelector('summary'));
    return clone;
  }

  _renderBar(facets) {
    this._renderDesktopBar(facets);
    this._renderMobileBar(facets);
  }

  _captureDesktopBarState() {
    if (!this.barRoot) return null;
    const openDetails = this.barRoot.querySelector('details[open]');
    const panel = openDetails?.querySelector('[data-constructor-dropdown-options]');
    const active = document.activeElement;
    const focusValue =
      active && openDetails?.contains(active) && active.dataset?.value != null ? String(active.dataset.value) : null;
    return {
      facetName: openDetails?.dataset.facetName,
      scrollTop: panel?.scrollTop ?? 0,
      focusValue,
      windowScrollX: window.scrollX,
      windowScrollY: window.scrollY,
    };
  }

  _restoreDesktopBarState(state) {
    if (!state) return;

    const details =
      state.facetName && this.barRoot
        ? this.barRoot.querySelector(`details[data-facet-name="${CSS.escape(state.facetName)}"]`)
        : null;
    if (details) details.setAttribute('open', '');
    const panel = details?.querySelector('[data-constructor-dropdown-options]');

    const applyScroll = () => {
      if (panel) panel.scrollTop = state.scrollTop;
      window.scrollTo(state.windowScrollX || 0, state.windowScrollY || 0);
    };
    applyScroll();
    requestAnimationFrame(applyScroll);

    if (details && state.focusValue != null) {
      const input = details.querySelector(`input[data-value="${CSS.escape(state.focusValue)}"]`);
      input?.focus({ preventScroll: true });
    }
  }

  _renderDesktopBar(facets) {
    if (!this.barRoot) return;
    const preserved = this._captureDesktopBarState();
    const fragment = document.createDocumentFragment();

    facets.forEach((facet, idx) => {
      if (this._isBooleanFacet(facet)) {
        const toggle = this._cloneBarToggle(facet, 'desktop');
        if (toggle) fragment.appendChild(toggle);
        return;
      }
      const dropdown = this._cloneDesktopDropdown(facet, idx);
      if (dropdown) fragment.appendChild(dropdown);
    });

    this.barRoot.innerHTML = '';
    this.barRoot.appendChild(fragment);
    this._restoreDesktopBarState(preserved);
  }

  _renderMobileBar(facets) {
    if (!this.barMobileRoot) return;
    const openerTpl = document.getElementById('constructor-facets-bar-opener');
    const fragment = document.createDocumentFragment();

    facets.forEach((facet) => {
      const facetName = facet.name;

      if (this._isBooleanFacet(facet)) {
        const toggle = this._cloneBarToggle(facet, 'mobile');
        if (toggle) fragment.appendChild(toggle);
        return;
      }

      if (!openerTpl) return;
      const selectedN = this._selected[facetName]?.size ?? 0;
      const clone = openerTpl.content.firstElementChild.cloneNode(true);
      clone.dataset.facetName = facetName;

      const titleEl = clone.querySelector('[data-constructor-bar-title]');
      if (titleEl) titleEl.textContent = this._facetDisplayName(facetName);

      const selectedEl = clone.querySelector('[data-constructor-bar-selected]');
      if (selectedEl && selectedN > 0) {
        selectedEl.textContent = String(selectedN);
        selectedEl.classList.remove('hidden');
      }

      this._appendCaret(clone);
      fragment.appendChild(clone);
    });

    this.barMobileRoot.innerHTML = '';
    this.barMobileRoot.appendChild(fragment);
  }

  _allFacetInputs() {
    const roots = [this.innerContainer, this.barRoot, this.barMobileRoot].filter(Boolean);
    return roots.flatMap((root) => [...root.querySelectorAll('input[data-facet-name]')]);
  }

  _syncInputs(facetName, val, checked) {
    this._allFacetInputs().forEach((el) => {
      if (el.dataset.facetName === facetName && el.dataset.value === val) {
        el.checked = checked;
      }
    });
  }

  _setOptionSelected(facetName, val, checked) {
    const set = this._ensureSet(facetName);
    if (checked) set.add(val);
    else set.delete(val);
    if (set.size === 0) delete this._selected[facetName];
    this._syncInputs(facetName, val, checked);
  }

  _onBarChange(event) {
    const cb = event.target;
    if (!cb || cb.type !== 'checkbox' || !cb.dataset.facetName) return;
    const facetName = cb.dataset.facetName;
    const val = cb.dataset.value;
    if (!facetName || val === undefined) return;

    cb.focus?.({ preventScroll: true });
    this._setOptionSelected(facetName, val, cb.checked);
    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _onDesktopBarClick(event) {
    const applyBtn = event.target.closest('[data-constructor-dropdown-apply]');
    if (!applyBtn) return;
    event.preventDefault();
    applyBtn.closest('details')?.removeAttribute('open');
  }

  _onBarClick(event) {
    const opener = event.target.closest('[data-bar-open]');
    if (!opener) return;
    event.preventDefault();

    const drawer = this._drawerRoot;
    if (!drawer) return;
    drawer.classList.add('menu-opening');
    drawer.setAttribute('open', '');
    document.body.classList.add(
      window.innerWidth < ConstructorBase.MOBILE_BODY_LOCK_PX ? 'overflow-hidden-mobile' : 'overflow-hidden',
    );

    FocusLayer.push(drawer, {
      contain: true,
      modal: true,
      lockScroll: false,
      trigger: opener,
      onDismiss: () => this._closeDrawer(),
    });

    const targetName = opener.dataset.facetName;
    if (targetName && this.innerContainer) {
      const targetBlock = this.innerContainer.querySelector(
        `.constructor-facet-details[data-facet-name="${CSS.escape(targetName)}"]`,
      );
      targetBlock?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  _ensureSet(facetName) {
    if (!this._selected[facetName]) this._selected[facetName] = new Set();
    return this._selected[facetName];
  }

  _onChange(event) {
    const cb = event.target;
    if (cb.type !== 'checkbox' || !cb.dataset.facetName) return;
    const facetName = cb.dataset.facetName;
    const val = cb.dataset.value;
    if (!facetName || val === undefined) return;

    this._setOptionSelected(facetName, val, cb.checked);
    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _updateSummaryCounts() {
    const roots = [this.innerContainer, this.barRoot, this.barMobileRoot].filter(Boolean);
    roots.forEach((root) => {
      root.querySelectorAll('[data-facet-name]').forEach((block) => {
        const fname = block.dataset.facetName;
        if (!fname) return;
        const n = this._selected[fname]?.size ?? 0;
        const text = n ? String(n) : '';
        const applyCountText = n ? `(${n})` : '';
        block
          .querySelectorAll(
            '.facets__selected, .active_value_count, .active_value_count_button, [data-constructor-bar-selected]',
          )
          .forEach((el) => {
            el.textContent = text;
            el.classList.toggle('hidden', !n);
          });
        block.querySelectorAll('[data-constructor-dropdown-apply-count]').forEach((el) => {
          el.textContent = applyCountText;
          el.classList.toggle('hidden', !n);
        });
      });
    });
    this._refreshFadeouts();
    this._updateDrawerCount();
  }

  _updateDrawerCount() {
    const total = Object.values(this._selected).reduce((sum, set) => sum + (set?.size || 0), 0);
    this._drawerRoot?.querySelectorAll('[data-constructor-drawer-count]').forEach((badge) => {
      badge.textContent = total ? String(total) : '';
      badge.classList.toggle('hidden', !total);
    });
    this._drawerRoot?.querySelectorAll('.mobile-facets__footer-clear').forEach((clear) => {
      clear.classList.toggle('hidden', !total);
    });
  }

  _onDelegClick(event) {
    const removeBtn = event.target.closest('[data-constructor-chip-remove]');
    if (!removeBtn) return;
    event.preventDefault();
    const facetName = removeBtn.dataset.facetName;
    const val = removeBtn.dataset.value;
    if (!facetName || val === undefined) return;

    this._setOptionSelected(facetName, val, false);

    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }

  _syncActiveChipsUi() {
    if (!this.chipsRoot) return;
    if (!this._mappingReady) return;

    const chipTpl = document.getElementById('constructor-facets-chip');
    const booleanFacetNames = new Set(
      (this._lastFacets || []).filter((f) => f?.name && this._isBooleanFacet(f)).map((f) => f.name),
    );

    const fragment = document.createDocumentFragment();
    let count = 0;

    Object.keys(this._selected)
      .sort()
      .forEach((fname) => {
        [...this._selected[fname]].forEach((val) => {
          if (!chipTpl) return;
          const isBooleanFacet = booleanFacetNames.has(fname);
          const cached = this._optionDisplayNames?.[fname]?.[val];
          const label = isBooleanFacet ? this._facetDisplayName(fname) : this._optionLabel(fname, String(val), cached);

          const clone = chipTpl.content.firstElementChild.cloneNode(true);

          const link = clone.querySelector('[data-constructor-chip-remove]');
          if (link) {
            link.dataset.facetName = fname;
            link.dataset.value = String(val);
          }

          const labelEl = clone.querySelector('[data-constructor-chip-label]');
          if (labelEl) labelEl.textContent = String(label);

          // Insert chip-remove icon between label and the visually-hidden hint so the
          // rendered children order matches the original (label, svg, hint).
          const inner = clone.querySelector('[data-constructor-chip-inner]');
          if (inner && this._chipRemoveHtml) {
            const visuallyHidden = inner.querySelector('.visually-hidden');
            const tmp = document.createElement('div');
            tmp.innerHTML = this._chipRemoveHtml;
            while (tmp.firstChild) inner.insertBefore(tmp.firstChild, visuallyHidden);
          }

          fragment.appendChild(clone);
          count++;
        });
      });

    this.chipsRoot.innerHTML = '';
    this.chipsRoot.appendChild(fragment);
    const any = count > 0;
    this.activeWrap?.classList.toggle('hidden', !any);
    this.activeHeading?.classList.toggle('hidden', !any);
    this._chipsClear?.classList.toggle('hidden', !any);
  }

  _broadcastFilters() {
    const filters = {};
    Object.keys(this._selected).forEach((k) => {
      const values = [...this._selected[k]];
      if (values.length) filters[k] = values;
    });
    this._browse?.dispatchEvent(
      new CustomEvent(this._constructorApplyEvent || 'constructor-browse:apply-filters', {
        bubbles: false,
        detail: { filters },
      }),
    );
    this._pushUrlState();
  }

  _pushUrlState() {
    const url = new URL(window.location.href);
    const managed = this._managedFilterParams();
    [...url.searchParams.keys()].forEach((k) => {
      if (managed.has(k)) url.searchParams.delete(k);
    });
    Object.keys(this._selected).forEach((fname) => {
      const cfg = this._facetConfig(fname);
      if (!cfg?.shopifyFilterParam) return;
      const facet = (this._lastFacets || []).find((f) => f?.name === fname);
      const isToggle = cfg.displayType === 'toggle' || (facet && this._isBooleanFacet(facet));
      [...this._selected[fname]].forEach((v) => {
        const urlVal = isToggle && cfg.toggleUrlValue ? cfg.toggleUrlValue : v;
        if (urlVal != null && urlVal !== '') {
          url.searchParams.append(cfg.shopifyFilterParam, urlVal);
        }
      });
    });
    if (window.location.search !== url.search) {
      window.history.replaceState({}, '', url);
    }
  }

  // Populates `_selected` from the URL on connect. MUST NOT call _broadcastFilters /
  // _pushUrlState — the URL is already the source of truth at this stage; pushing back
  // would create a hydration loop and racy filter dispatches before data arrives.
  _hydrateFromUrl() {
    const configs = window.constructorFacetsConfig || [];
    if (!configs.length) return;
    const url = new URL(window.location.href);
    url.searchParams.forEach((urlVal, key) => {
      if (!key.startsWith('filter.')) return;
      configs
        .filter((c) => String(c.shopifyFilterParam || '').trim() === key)
        .forEach((c) => {
          const isToggle =
            String(c.displayType || '')
              .toLowerCase()
              .trim() === 'toggle';
          if (isToggle) {
            // Falls back to 'True' for facets that don't have toggle_constructor_value set yet.
            if (String(c.toggleUrlValue || '').trim() === urlVal) {
              this._ensureSet(c.name).add(String(c.toggleConstructorValue || 'True'));
            }
          } else {
            this._ensureSet(c.name).add(urlVal);
          }
        });
    });
  }

  _onClearAll(event) {
    event?.preventDefault?.();
    this._selected = Object.create(null);
    this._allFacetInputs().forEach((cb) => {
      cb.checked = false;
    });
    this._broadcastFilters();
    this._updateSummaryCounts();
    this._syncActiveChipsUi();
  }
}

customElements.define('constructor-facets', ConstructorFacets);
